import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Team, TeamMember } from '../types';
import { generateInvitationCode } from '../utils/invitationUtils';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

const DEBUG_TEAM_SERVICE = false;
const debugLog = (...args: unknown[]) => {
  if (DEBUG_TEAM_SERVICE) {
    console.log(...args);
  }
};

export const teamService = {
  // Create a new team
  async createTeam(teamData: Omit<Team, 'id' | 'createdAt' | 'updatedAt' | 'invitationCode'>): Promise<string> {
    const invitationCode = generateInvitationCode();
    
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      debugLog('createTeam: iOS detected, using REST API');
      
      try {
        // Create team document via REST API
        const teamDocumentData: any = {
          ...teamData,
          invitationCode,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        const teamId = await firestoreRestApi.createDocument('teams', teamDocumentData);
        debugLog('createTeam: Team created via REST API with ID:', teamId);
        
        // Automatically add the head coach as a team member with headCoach role
        // addTeamMember already has iOS workaround, but we need to ensure it sets the role correctly
        const memberId = await this.addTeamMember(teamId, teamData.headCoachId);
        debugLog('createTeam: Member added with ID:', memberId);
        
        // Update the member role to headCoach via REST API
        const memberUpdateData = {
          role: 'headCoach',
          assignedBy: teamData.headCoachId,
          assignedAt: new Date(),
        };
        await firestoreRestApi.setDocument('teamMembers', memberId, memberUpdateData, true); // true = merge mode
        debugLog('createTeam: Member role updated to headCoach via REST API');
        
        return teamId;
      } catch (error: any) {
        console.error('createTeam: Error via REST API:', error);
        throw new Error(error.message || 'Failed to create team');
      }
    }
    
    // Web/Android - use Firebase SDK
    const docRef = await addDoc(collection(db, 'teams'), {
      ...teamData,
      invitationCode,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // Automatically add the head coach as a team member with headCoach role
    const memberId = await this.addTeamMember(docRef.id, teamData.headCoachId);
    // Set the head coach role
    const memberRef = doc(db, 'teamMembers', memberId);
    await updateDoc(memberRef, {
      role: 'headCoach',
      assignedBy: teamData.headCoachId,
      assignedAt: serverTimestamp(),
    });
    
    return docRef.id;
  },

  // Get all teams for a coach (head coach, coach, or admin)
  // This function now returns teams where the user is head coach, or is a coach/admin member
  async getTeamsByCoach(coachId: string): Promise<Team[]> {
    if (!coachId) {
      console.warn('getTeamsByCoach: coachId is undefined or empty');
      return [];
    }
    
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        // First, get teams where user is head coach
        const headCoachDocs = await firestoreRestApi.queryDocuments(
          'teams',
          [{ field: 'headCoachId', op: '==', value: coachId }]
        );
        
        // Also check for legacy coachId field (backward compatibility)
        const legacyCoachDocs = await firestoreRestApi.queryDocuments(
          'teams',
          [{ field: 'coachId', op: '==', value: coachId }]
        );
        
        // Combine and deduplicate
        const allDocs = [...headCoachDocs, ...legacyCoachDocs];
        const uniqueDocs = allDocs.filter((doc, index, self) => 
          index === self.findIndex(d => d.id === doc.id)
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = uniqueDocs.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
        })) as Team[];
      } catch (error) {
        console.error('Error fetching coach teams via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    debugLog('getTeamsByCoach: Querying for teams with headCoachId:', coachId);
    debugLog('getTeamsByCoach: coachId type:', typeof coachId, 'value:', JSON.stringify(coachId));
    try {
      // Get teams where user is head coach
      const headCoachQuery = query(
        collection(db, 'teams'),
        where('headCoachId', '==', coachId),
        orderBy('createdAt', 'desc')
      );
      const headCoachSnapshot = await getDocs(headCoachQuery);
      debugLog('getTeamsByCoach: Found', headCoachSnapshot.docs.length, 'teams with headCoachId');
      
      // Debug: Log all teams to see what headCoachIds exist
      if (headCoachSnapshot.docs.length === 0) {
        console.warn('getTeamsByCoach: No teams found with headCoachId:', coachId);
        debugLog('getTeamsByCoach: Attempting to query all teams to debug...');
        try {
          const allTeamsQuery = query(collection(db, 'teams'), limit(10));
          const allTeamsSnapshot = await getDocs(allTeamsQuery);
          debugLog('getTeamsByCoach: Sample teams in database:', allTeamsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            headCoachId: doc.data().headCoachId,
            coachId: (doc.data() as any).coachId
          })));
        } catch (debugError) {
          console.warn('getTeamsByCoach: Could not query all teams for debugging:', debugError);
        }
      }
      
      // Also check for legacy coachId field (backward compatibility)
      let legacyCoachSnapshot;
      try {
        const legacyCoachQuery = query(
          collection(db, 'teams'),
          where('coachId', '==', coachId),
          orderBy('createdAt', 'desc')
        );
        legacyCoachSnapshot = await getDocs(legacyCoachQuery);
        debugLog('getTeamsByCoach: Found', legacyCoachSnapshot.docs.length, 'teams with legacy coachId');
      } catch (legacyError: any) {
        console.warn('getTeamsByCoach: Error querying legacy coachId (may not have index):', legacyError.message);
        legacyCoachSnapshot = { docs: [] } as any;
      }
      
      // Combine and deduplicate
      const allDocs = [...headCoachSnapshot.docs, ...legacyCoachSnapshot.docs];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );
      
      debugLog('getTeamsByCoach: Total unique teams found:', uniqueDocs.length);
      const teams = uniqueDocs.map(doc => {
        const data = doc.data();
        debugLog('getTeamsByCoach: Team found:', doc.id, 'headCoachId:', data.headCoachId, 'coachId:', (data as any).coachId);
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as Team;
      });
      
      return teams;
    } catch (error: any) {
      console.error('getTeamsByCoach: Error querying teams:', error);
      console.error('getTeamsByCoach: Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // If the error is about missing index, try querying without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        debugLog('getTeamsByCoach: Index error detected, trying query without orderBy...');
        try {
          const headCoachQueryNoOrder = query(
            collection(db, 'teams'),
            where('headCoachId', '==', coachId)
          );
          const headCoachSnapshot = await getDocs(headCoachQueryNoOrder);
          debugLog('getTeamsByCoach: Found', headCoachSnapshot.docs.length, 'teams (without orderBy)');
          
          // Sort client-side
          const teams = headCoachSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt,
            updatedAt: doc.data().updatedAt,
          })) as Team[];
          
          // Sort by createdAt descending (client-side)
          teams.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0;
            return bTime - aTime;
          });
          
          return teams;
        } catch (fallbackError: any) {
          console.error('getTeamsByCoach: Fallback query also failed:', fallbackError);
          return [];
        }
      }
      
      return [];
    }
  },

  // Get all teams for an athlete
  async getTeamsByAthlete(athleteId: string): Promise<Team[]> {
    if (!athleteId) {
      console.warn('getTeamsByAthlete: athleteId is undefined or empty');
      return [];
    }
    
    debugLog('getTeamsByAthlete: Starting for athleteId:', athleteId);
    
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        // First get all team memberships for this athlete
        const memberDocuments = await firestoreRestApi.queryDocuments(
          'teamMembers',
          [{ field: 'athleteId', op: '==', value: athleteId }]
        );
        
        debugLog('getTeamsByAthlete: Found', memberDocuments.length, 'memberships');
        
        if (memberDocuments.length === 0) {
          debugLog('getTeamsByAthlete: No memberships found');
          return [];
        }
        
        // Get team IDs from memberships
        // Handle both data() method and direct data access
        const teamIds = memberDocuments
          .map(doc => {
            const docData = typeof doc.data === 'function' ? doc.data() : doc;
            return docData.teamId;
          })
          .filter((teamId): teamId is string => !!teamId); // Filter out undefined/null teamIds
        debugLog('getTeamsByAthlete: Team IDs:', teamIds);
        
        if (teamIds.length === 0) {
          debugLog('getTeamsByAthlete: No valid team IDs found');
          return [];
        }
        
        // Fetch team details for each team ID
        const teamPromises = teamIds.map(async (teamId) => {
          try {
            const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
            if (teamDoc) {
              debugLog('getTeamsByAthlete: Found team:', teamId);
              // Handle both data() method and direct data access
              const teamData = typeof teamDoc.data === 'function' ? teamDoc.data() : teamDoc;
              return {
                id: teamDoc.id,
                ...teamData,
                createdAt: teamData.createdAt || Timestamp.now(),
                updatedAt: teamData.updatedAt || Timestamp.now(),
              } as Team;
            }
            debugLog('getTeamsByAthlete: Team not found:', teamId);
            return null;
          } catch (error) {
            console.error('getTeamsByAthlete: Error fetching team:', teamId, error);
            return null;
          }
        });
        
        const teams = await Promise.all(teamPromises);
        const validTeams = teams.filter(team => team !== null) as Team[];
        debugLog('getTeamsByAthlete: Returning', validTeams.length, 'teams');
        return validTeams;
      } catch (error: any) {
        console.error('getTeamsByAthlete: Error via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    try {
      // First get all team memberships for this athlete
      const memberQuery = query(
        collection(db, 'teamMembers'),
        where('athleteId', '==', athleteId)
      );
      const memberSnapshot = await getDocs(memberQuery);
      
      debugLog('getTeamsByAthlete: Found', memberSnapshot.docs.length, 'memberships');
      
      if (memberSnapshot.empty) {
        debugLog('getTeamsByAthlete: No memberships found');
        return [];
      }
      
      // Get team IDs from memberships
      const teamIds = memberSnapshot.docs
        .map(doc => doc.data().teamId)
        .filter((teamId): teamId is string => !!teamId); // Filter out undefined/null teamIds
      debugLog('getTeamsByAthlete: Team IDs:', teamIds);
      
      if (teamIds.length === 0) {
        debugLog('getTeamsByAthlete: No valid team IDs found');
        return [];
      }
      
      // Fetch team details for each team ID
      const teamPromises = teamIds.map(async (teamId) => {
        const teamDoc = await getDoc(doc(db, 'teams', teamId));
        if (teamDoc.exists()) {
          debugLog('getTeamsByAthlete: Found team:', teamId);
          return {
            id: teamDoc.id,
            ...teamDoc.data(),
            createdAt: teamDoc.data().createdAt,
            updatedAt: teamDoc.data().updatedAt,
          } as Team;
        }
        debugLog('getTeamsByAthlete: Team not found:', teamId);
        return null;
      });
      
      const teams = await Promise.all(teamPromises);
      const validTeams = teams.filter(team => team !== null) as Team[];
      debugLog('getTeamsByAthlete: Returning', validTeams.length, 'teams');
      return validTeams;
    } catch (error: any) {
      console.error('getTeamsByAthlete: Error (SDK):', error);
      return [];
    }
  },

  // Get team by ID
  async getTeamById(teamId: string): Promise<Team | null> {
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    if (!teamDoc.exists()) return null;
    
    return {
      id: teamDoc.id,
      ...teamDoc.data(),
      createdAt: teamDoc.data().createdAt,
      updatedAt: teamDoc.data().updatedAt,
    } as Team;
  },

  // Clean up orphaned team memberships (memberships for teams that no longer exist)
  async cleanupOrphanedMemberships(athleteId: string): Promise<void> {
    debugLog('cleanupOrphanedMemberships: Starting cleanup for athlete:', athleteId);
    
    // Get all team memberships for this athlete
    const memberQuery = query(
      collection(db, 'teamMembers'),
      where('athleteId', '==', athleteId)
    );
    const memberSnapshot = await getDocs(memberQuery);
    
    debugLog('cleanupOrphanedMemberships: Found', memberSnapshot.docs.length, 'memberships');
    
    const deletePromises = [];
    
    for (const memberDoc of memberSnapshot.docs) {
      const memberData = memberDoc.data();
      const teamId = memberData.teamId;
      
      // Check if the team still exists
      const teamDoc = await getDoc(doc(db, 'teams', teamId));
      if (!teamDoc.exists()) {
        debugLog('cleanupOrphanedMemberships: Deleting orphaned membership for team:', teamId);
        deletePromises.push(deleteDoc(doc(db, 'teamMembers', memberDoc.id)));
      } else {
        debugLog('cleanupOrphanedMemberships: Keeping membership for existing team:', teamId);
      }
    }
    
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      debugLog('cleanupOrphanedMemberships: Deleted', deletePromises.length, 'orphaned memberships');
    } else {
      debugLog('cleanupOrphanedMemberships: No orphaned memberships found');
    }
  },

  // Ensure coach is added as a member of their own teams (for existing coaches)
  async ensureCoachMembership(coachId: string): Promise<void> {
    if (!coachId) {
      console.warn('ensureCoachMembership: coachId is undefined or empty');
      return;
    }
    
    debugLog('ensureCoachMembership: Starting for coachId:', coachId);
    debugLog('ensureCoachMembership: coachId type:', typeof coachId, 'value:', JSON.stringify(coachId));
    
    try {
      // Get all teams owned by this coach
      const coachTeams = await this.getTeamsByCoach(coachId);
      debugLog('ensureCoachMembership: Found', coachTeams.length, 'coach-owned teams');
      
      // If no teams found via getTeamsByCoach, try alternative approach for headCoach
      // Sometimes teams might exist but headCoachId doesn't match exactly
      if (coachTeams.length === 0) {
        console.warn('ensureCoachMembership: No teams found via getTeamsByCoach, checking if user is headCoach...');
        try {
          // Get user document to check role
          const userDoc = await getDoc(doc(db, 'users', coachId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userRole = userData.role;
            debugLog('ensureCoachMembership: User role:', userRole);
            
            // If headCoach, try querying all teams and checking headCoachId manually
            // This helps catch cases where headCoachId might be stored differently
            if (userRole === 'headCoach') {
              debugLog('ensureCoachMembership: User is headCoach, trying alternative query...');
              const allTeamsQuery = query(collection(db, 'teams'), limit(50));
              const allTeamsSnapshot = await getDocs(allTeamsQuery);
              const matchingTeams = allTeamsSnapshot.docs
                .filter(doc => {
                  const data = doc.data();
                  const headCoachId = data.headCoachId;
                  const legacyCoachId = (data as any).coachId;
                  const matches = headCoachId === coachId || legacyCoachId === coachId;
                  if (matches) {
                    debugLog('ensureCoachMembership: Found matching team via alternative query:', doc.id, {
                      headCoachId,
                      legacyCoachId,
                      searchingFor: coachId
                    });
                  }
                  return matches;
                })
                .map(doc => ({
                  id: doc.id,
                  ...doc.data(),
                  createdAt: doc.data().createdAt,
                  updatedAt: doc.data().updatedAt,
                })) as Team[];
              
              if (matchingTeams.length > 0) {
                debugLog('ensureCoachMembership: Found', matchingTeams.length, 'teams via alternative query');
                coachTeams.push(...matchingTeams);
              }
            }
          }
        } catch (userError) {
          console.warn('ensureCoachMembership: Error checking user role:', userError);
        }
      }
      
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        debugLog('ensureCoachMembership: iOS detected, using REST API');
        
        // Check if coach is already a member of each team
        for (const team of coachTeams) {
          const memberId = `${team.id}_${coachId}`;
          const memberDoc = await firestoreRestApi.getDocument('teamMembers', memberId);
          
          if (!memberDoc) {
            debugLog('ensureCoachMembership: Adding coach/headCoach as member of team:', team.id);
            try {
              await this.addTeamMember(team.id, coachId);
            } catch (addError: any) {
              console.warn('ensureCoachMembership: Failed to add coach as member via addTeamMember:', addError);
              
              // Fallback: Try direct document creation with correct role via REST API
              try {
                // Get user's role from users collection via REST API
                const userDoc = await firestoreRestApi.getDocument('users', coachId);
                let memberRole: 'athlete' | 'coach' | 'headCoach' = 'athlete';
                if (userDoc) {
                  const userData = userDoc.data ? userDoc.data() : userDoc;
                  const userRole = userData?.role || 'athlete';
                  if (userRole === 'headCoach') {
                    memberRole = 'headCoach';
                  } else if (userRole === 'coach') {
                    memberRole = 'coach';
                  }
                }
                
                const memberData = {
                  teamId: team.id,
                  athleteId: coachId,
                  role: memberRole,
                  joinedAt: new Date(),
                };
                debugLog('ensureCoachMembership: Trying direct document creation with role:', memberRole);
                await firestoreRestApi.setDocument('teamMembers', memberId, memberData);
                debugLog('ensureCoachMembership: Direct document creation successful with role:', memberRole);
              } catch (directError) {
                console.error('ensureCoachMembership: Direct document creation also failed:', directError);
                // Don't throw - this is a non-critical operation
                console.warn('ensureCoachMembership: Skipping team membership for team:', team.id);
              }
            }
          } else {
            debugLog('ensureCoachMembership: Coach already a member of team:', team.id);
          }
        }
        
        debugLog('ensureCoachMembership: Completed successfully (iOS)');
        return;
      }
      
      // Web/Android - use Firebase SDK
      // Check if coach is already a member of each team
      for (const team of coachTeams) {
        const memberId = `${team.id}_${coachId}`;
        const memberDoc = await getDoc(doc(db, 'teamMembers', memberId));
        
        if (!memberDoc.exists()) {
          debugLog('ensureCoachMembership: Adding coach/headCoach as member of team:', team.id);
          try {
            await this.addTeamMember(team.id, coachId);
          } catch (addError: any) {
            console.warn('ensureCoachMembership: Failed to add coach as member via addTeamMember:', addError);
            
            // Fallback: Try direct document creation with correct role
            try {
              // Get user's role from users collection
              const userDoc = await getDoc(doc(db, 'users', coachId));
              let memberRole: 'athlete' | 'coach' | 'headCoach' = 'athlete';
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const userRole = userData?.role || 'athlete';
                if (userRole === 'headCoach') {
                  memberRole = 'headCoach';
                } else if (userRole === 'coach') {
                  memberRole = 'coach';
                }
              }
              
              const memberData = {
                teamId: team.id,
                athleteId: coachId,
                role: memberRole,
                joinedAt: serverTimestamp()
              };
              debugLog('ensureCoachMembership: Trying direct document creation with role:', memberRole);
              await setDoc(doc(db, 'teamMembers', memberId), memberData);
              debugLog('ensureCoachMembership: Direct document creation successful with role:', memberRole);
            } catch (directError) {
              console.error('ensureCoachMembership: Direct document creation also failed:', directError);
              // Don't throw - this is a non-critical operation
              console.warn('ensureCoachMembership: Skipping team membership for team:', team.id);
            }
          }
        } else {
          debugLog('ensureCoachMembership: Coach already a member of team:', team.id);
        }
      }
      
      debugLog('ensureCoachMembership: Completed successfully');
    } catch (error) {
      console.error('ensureCoachMembership: Error:', error);
      // Don't throw - this is a non-critical operation that shouldn't break the app
      console.warn('ensureCoachMembership: Continuing despite errors');
    }
  },

  // Update team
  async updateTeam(teamId: string, updates: Partial<Omit<Team, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    const teamRef = doc(db, 'teams', teamId);
    await updateDoc(teamRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete team
  async deleteTeam(teamId: string): Promise<void> {
    await deleteDoc(doc(db, 'teams', teamId));
  },

  // Add member to team
  async addTeamMember(teamId: string, athleteId: string): Promise<string> {
    const memberId = `${teamId}_${athleteId}`;
    debugLog('addTeamMember: Creating member with ID:', memberId);
    
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      debugLog('addTeamMember: iOS detected, using REST API');
      
      // Get the team to check if this user is the head coach
      const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
      let memberRole: 'athlete' | 'coach' | 'headCoach' = 'athlete';
      
      if (teamDoc) {
        const teamData = teamDoc as Team;
        // Check if user is the head coach of this team
        if (teamData.headCoachId === athleteId || (teamData as any).coachId === athleteId) {
          // Get user's role from users collection
          const userDoc = await firestoreRestApi.getDocument('users', athleteId);
          if (userDoc) {
            const userRole = userDoc.role || 'athlete';
            if (userRole === 'headCoach') {
              memberRole = 'headCoach';
            } else if (userRole === 'coach') {
              memberRole = 'coach';
            }
          }
        } else {
          // For non-head-coach members, get their role from users collection
          const userDoc = await firestoreRestApi.getDocument('users', athleteId);
          if (userDoc) {
            const userRole = userDoc.role || 'athlete';
            if (userRole === 'headCoach' || userRole === 'coach') {
              memberRole = userRole === 'headCoach' ? 'headCoach' : 'coach';
            }
          }
        }
      }
      
      const memberData: any = {
        id: memberId,
        teamId,
        athleteId,
        role: memberRole,
        joinedAt: new Date(),
      };
      debugLog('addTeamMember: Member data with role:', memberData);
      await firestoreRestApi.setDocument('teamMembers', memberId, memberData);
      debugLog('addTeamMember: Successfully created member document with role:', memberRole);
      return memberId;
    }
    
    // Web version - use Firebase SDK
    // Get the team to check if this user is the head coach
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    let memberRole: 'athlete' | 'coach' | 'headCoach' = 'athlete';
    
    if (teamDoc.exists()) {
      const teamData = teamDoc.data() as Team;
      // Check if user is the head coach of this team
      if (teamData.headCoachId === athleteId || (teamData as any).coachId === athleteId) {
        // Get user's role from users collection to determine if they're headCoach or coach
        const userDoc = await getDoc(doc(db, 'users', athleteId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userRole = userData?.role || 'athlete';
          if (userRole === 'headCoach') {
            memberRole = 'headCoach';
          } else if (userRole === 'coach') {
            memberRole = 'coach';
          }
        }
      } else {
        // For non-head-coach members, get their role from users collection
        const userDoc = await getDoc(doc(db, 'users', athleteId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userRole = userData?.role || 'athlete';
          if (userRole === 'headCoach' || userRole === 'coach') {
            memberRole = userRole === 'headCoach' ? 'headCoach' : 'coach';
          }
        }
      }
    }
    
    const memberData: Omit<TeamMember, 'id'> = {
      teamId,
      athleteId,
      role: memberRole,
      joinedAt: serverTimestamp() as any,
    };
    debugLog('addTeamMember: Member data with role:', memberData);
    const docRef = doc(db, 'teamMembers', memberId);
    await setDoc(docRef, memberData);
    debugLog('addTeamMember: Successfully created member document with role:', memberRole);
    return memberId;
  },

  // Get team members
  async getTeamMembers(teamId: string): Promise<TeamMember[]> {
    debugLog('getTeamMembers: Starting for teamId:', teamId);
    
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        // First, check the team document
        try {
          const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
          if (teamDoc) {
            const teamData = teamDoc.data();
            debugLog('getTeamMembers: Team document exists, coachId:', teamData.coachId);
          } else {
            debugLog('getTeamMembers: Team document does not exist!');
          }
        } catch (error) {
          debugLog('getTeamMembers: Error checking team document:', error);
        }
        
        // Get team members
        const documents = await firestoreRestApi.queryDocuments(
          'teamMembers',
          [{ field: 'teamId', op: '==', value: teamId }]
        );
        
        debugLog('getTeamMembers: Found', documents.length, 'members for team', teamId);
        
        // Sort by joinedAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().joinedAt?.seconds || 0;
          const bTime = b.data().joinedAt?.seconds || 0;
          return bTime - aTime;
        });
        
        const members = sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          joinedAt: doc.data().joinedAt || Timestamp.now(),
        })) as TeamMember[];
        
        debugLog('getTeamMembers: Returning members:', members);
        return members;
      } catch (error) {
        console.error('getTeamMembers: Error via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    // First, let's check the team document to see what coachId it has
    try {
      const teamDoc = await getDoc(doc(db, 'teams', teamId));
      if (teamDoc.exists()) {
        const teamData = teamDoc.data();
        debugLog('getTeamMembers: Team document exists, coachId:', teamData.coachId);
      } else {
        debugLog('getTeamMembers: Team document does not exist!');
      }
    } catch (error) {
      debugLog('getTeamMembers: Error checking team document:', error);
    }
    
    const q = query(
      collection(db, 'teamMembers'),
      where('teamId', '==', teamId),
      orderBy('joinedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    debugLog('getTeamMembers: Found', querySnapshot.docs.length, 'members for team', teamId);
    
    const members = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      joinedAt: doc.data().joinedAt,
    })) as TeamMember[];
    
    debugLog('getTeamMembers: Returning members:', members);
    return members;
  },

  // Remove member from team
  async removeTeamMember(memberId: string): Promise<void> {
    await deleteDoc(doc(db, 'teamMembers', memberId));
  },

  // Join team by invitation code
  async joinTeamByCode(invitationCode: string, athleteId: string): Promise<{ success: boolean; teamId?: string; error?: string }> {
    try {
      debugLog('joinTeamByCode: Starting with code:', invitationCode, 'athleteId:', athleteId);
      
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        debugLog('joinTeamByCode: iOS detected, using REST API');
        
        // Find team by invitation code
        const teamDocs = await firestoreRestApi.queryDocuments(
          'teams',
          [{ field: 'invitationCode', op: '==', value: invitationCode }],
          1
        );
        
        if (teamDocs.length === 0) {
          debugLog('joinTeamByCode: No team found with code:', invitationCode);
          return { success: false, error: 'Invalid invitation code' };
        }
        
        const teamId = teamDocs[0].id;
        debugLog('joinTeamByCode: Found team:', teamId);
        
        // Check if athlete is already a member
        const memberDocs = await firestoreRestApi.queryDocuments(
          'teamMembers',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'athleteId', op: '==', value: athleteId }
          ],
          1
        );
        
        if (memberDocs.length > 0) {
          debugLog('joinTeamByCode: Athlete already a member');
          return { success: false, error: 'You are already a member of this team' };
        }
        
        // Add athlete to team
        debugLog('joinTeamByCode: Adding athlete to team...');
        const memberId = await this.addTeamMember(teamId, athleteId);
        debugLog('joinTeamByCode: Successfully added member with ID:', memberId);
        
        // Update user's teamId field (merge mode to preserve other fields)
        debugLog('joinTeamByCode: Updating user teamId...');
        await firestoreRestApi.setDocument('users', athleteId, { teamId }, true); // true = merge mode
        debugLog('joinTeamByCode: User teamId updated');
        
        return { success: true, teamId };
      }
      
      // Web version - use Firebase SDK
      // Find team by invitation code
      const q = query(
        collection(db, 'teams'),
        where('invitationCode', '==', invitationCode)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        debugLog('joinTeamByCode: No team found with code:', invitationCode);
        return { success: false, error: 'Invalid invitation code' };
      }
      
      const teamDoc = querySnapshot.docs[0];
      const teamId = teamDoc.id;
      debugLog('joinTeamByCode: Found team:', teamId);
      
      // Check if athlete is already a member
      const memberQuery = query(
        collection(db, 'teamMembers'),
        where('teamId', '==', teamId),
        where('athleteId', '==', athleteId)
      );
      const memberSnapshot = await getDocs(memberQuery);
      
      if (!memberSnapshot.empty) {
        debugLog('joinTeamByCode: Athlete already a member');
        return { success: false, error: 'You are already a member of this team' };
      }
      
      // Add athlete to team
      debugLog('joinTeamByCode: Adding athlete to team...');
      const memberId = await this.addTeamMember(teamId, athleteId);
      debugLog('joinTeamByCode: Successfully added member with ID:', memberId);
      
      // Update user's teamId field
      debugLog('joinTeamByCode: Updating user teamId...');
      await updateDoc(doc(db, 'users', athleteId), { teamId });
      debugLog('joinTeamByCode: User teamId updated');
      
      return { success: true, teamId };
    } catch (error: any) {
      console.error('joinTeamByCode: Error:', error);
      return { success: false, error: error.message || 'Failed to join team' };
    }
  },

  // Regenerate invitation code for a team
  async regenerateInvitationCode(teamId: string): Promise<string> {
    const newCode = generateInvitationCode();
    await updateDoc(doc(db, 'teams', teamId), {
      invitationCode: newCode,
      updatedAt: serverTimestamp(),
    });
    return newCode;
  },

  // Get team by invitation code
  async getTeamByInvitationCode(invitationCode: string): Promise<Team | null> {
    const q = query(
      collection(db, 'teams'),
      where('invitationCode', '==', invitationCode)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) return null;
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
    } as Team;
  },

  // Update athlete tags for event filtering
  async updateMemberTags(memberId: string, tags: string[]): Promise<void> {
    const memberRef = doc(db, 'teamMembers', memberId);
    await updateDoc(memberRef, {
      tags: tags.length > 0 ? tags : [], // Ensure empty array instead of undefined
    });
  },

  // Get members filtered by tag
  async getMembersByTag(teamId: string, tag: string): Promise<TeamMember[]> {
    const members = await this.getTeamMembers(teamId);
    return members.filter(member => member.tags && member.tags.includes(tag));
  },

  // Get member with tags
  async getMemberWithTags(memberId: string): Promise<TeamMember | null> {
    const memberDoc = await getDoc(doc(db, 'teamMembers', memberId));
    if (!memberDoc.exists()) return null;
    
    return {
      id: memberDoc.id,
      ...memberDoc.data(),
      joinedAt: memberDoc.data().joinedAt,
      tags: memberDoc.data().tags || [],
    } as TeamMember;
  },

  // ============================================
  // ROLE MANAGEMENT FUNCTIONS
  // ============================================

  // Get team member role information
  async getTeamMemberRole(teamId: string, userId: string): Promise<{ role: 'athlete' | 'coach' | 'headCoach' | null; isAdmin: boolean } | null> {
    const memberId = `${teamId}_${userId}`;
    
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        debugLog('getTeamMemberRole: iOS detected, using REST API');
        
        // Get team member document
        const memberDoc = await firestoreRestApi.getDocument('teamMembers', memberId);
        
        if (!memberDoc) {
          debugLog('getTeamMemberRole: Document does not exist:', memberId);
          
          // If team member doesn't exist, check if user is the head coach of the team
          // This handles cases where team was created but member wasn't added yet
          const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
          if (teamDoc) {
            const teamData = teamDoc.data ? teamDoc.data() : teamDoc;
            if (teamData.headCoachId === userId || (teamData as any).coachId === userId) {
              // User is head coach, get their role from users collection
              const userDoc = await firestoreRestApi.getDocument('users', userId);
              if (userDoc) {
                const userData = userDoc.data ? userDoc.data() : userDoc;
                const userRole = userData?.role || 'athlete';
                if (userRole === 'headCoach' || userRole === 'coach') {
                  debugLog('getTeamMemberRole: User is head coach but member doc missing, returning role from user:', userRole);
                  return {
                    role: userRole === 'headCoach' ? 'headCoach' : 'coach',
                    isAdmin: false,
                  };
                }
              }
            }
          }
          
          return null;
        }
        
        const memberData = memberDoc.data ? memberDoc.data() : memberDoc;
        let role = memberData.role || 'athlete';
        
        // If role is missing or 'athlete', but user is the head coach, check user's role
        if (!memberData.role || memberData.role === 'athlete') {
          const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
          if (teamDoc) {
            const teamData = teamDoc.data ? teamDoc.data() : teamDoc;
            if (teamData.headCoachId === userId || (teamData as any).coachId === userId) {
              // User is head coach, get their role from users collection
              const userDoc = await firestoreRestApi.getDocument('users', userId);
              if (userDoc) {
                const userData = userDoc.data ? userDoc.data() : userDoc;
                const userRole = userData?.role || 'athlete';
                if (userRole === 'headCoach' || userRole === 'coach') {
                  debugLog('getTeamMemberRole: Member role was missing/athlete but user is head coach, using role from user:', userRole);
                  role = userRole === 'headCoach' ? 'headCoach' : 'coach';
                  // Optionally update the member document with the correct role
                  try {
                    await firestoreRestApi.setDocument('teamMembers', memberId, { role }, true); // true = merge mode
                    debugLog('getTeamMemberRole: Updated member document with correct role');
                  } catch (updateError) {
                    console.warn('getTeamMemberRole: Failed to update member role:', updateError);
                  }
                }
              }
            }
          }
        }
        
        const roleInfo = {
          role: role as 'athlete' | 'coach' | 'headCoach',
          isAdmin: memberData.isAdmin || false,
        };
        
        debugLog('getTeamMemberRole: Found document:', {
          memberId,
          role: roleInfo.role,
          isAdmin: roleInfo.isAdmin,
          fullData: memberData,
          returnedRoleInfo: roleInfo,
        });
        
        return roleInfo;
      } catch (error) {
        console.error('getTeamMemberRole: Error via REST API:', error);
        return null;
      }
    }
    
    // Web/Android - use Firebase SDK
    const memberDoc = await getDoc(doc(db, 'teamMembers', memberId));
    
    if (!memberDoc.exists()) {
      debugLog('getTeamMemberRole: Document does not exist:', memberId);
      
      // If team member doesn't exist, check if user is the head coach of the team
      // This handles cases where team was created but member wasn't added yet
      const teamDoc = await getDoc(doc(db, 'teams', teamId));
      if (teamDoc.exists()) {
        const teamData = teamDoc.data() as Team;
        if (teamData.headCoachId === userId || (teamData as any).coachId === userId) {
          // User is head coach, get their role from users collection
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userRole = userData?.role || 'athlete';
            if (userRole === 'headCoach' || userRole === 'coach') {
              debugLog('getTeamMemberRole: User is head coach but member doc missing, returning role from user:', userRole);
              return {
                role: userRole === 'headCoach' ? 'headCoach' : 'coach',
                isAdmin: false,
              };
            }
          }
        }
      }
      
      return null;
    }
    
    const memberData = memberDoc.data() as TeamMember;
    let role = memberData.role || 'athlete';
    
    // If role is missing or 'athlete', but user is the head coach, check user's role
    if (!memberData.role || memberData.role === 'athlete') {
      const teamDoc = await getDoc(doc(db, 'teams', teamId));
      if (teamDoc.exists()) {
        const teamData = teamDoc.data() as Team;
        if (teamData.headCoachId === userId || (teamData as any).coachId === userId) {
          // User is head coach, get their role from users collection
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userRole = userData?.role || 'athlete';
            if (userRole === 'headCoach' || userRole === 'coach') {
              debugLog('getTeamMemberRole: Member role was missing/athlete but user is head coach, using role from user:', userRole);
              role = userRole === 'headCoach' ? 'headCoach' : 'coach';
              // Optionally update the member document with the correct role
              try {
                await updateDoc(doc(db, 'teamMembers', memberId), { role });
                debugLog('getTeamMemberRole: Updated member document with correct role');
              } catch (updateError) {
                console.warn('getTeamMemberRole: Failed to update member role:', updateError);
              }
            }
          }
        }
      }
    }
    
    const roleInfo = {
      role: role as 'athlete' | 'coach' | 'headCoach',
      isAdmin: memberData.isAdmin || false,
    };
    
    debugLog('getTeamMemberRole: Found document:', {
      memberId,
      role: roleInfo.role,
      isAdmin: roleInfo.isAdmin,
      fullData: memberData,
      returnedRoleInfo: roleInfo,
    });
    
    return roleInfo;
  },

  // Check if user is admin
  async isAdmin(teamId: string, userId: string): Promise<boolean> {
    const roleInfo = await this.getTeamMemberRole(teamId, userId);
    return roleInfo?.isAdmin || false;
  },

  // Check if user can manage team (admin, head coach, or coach)
  async canManageTeam(teamId: string, userId: string): Promise<boolean> {
    const roleInfo = await this.getTeamMemberRole(teamId, userId);
    if (!roleInfo) return false;
    
    // Admin can always manage
    if (roleInfo.isAdmin) return true;
    
    // Head coach can manage
    if (roleInfo.role === 'headCoach') return true;
    
    // Regular coach can manage
    if (roleInfo.role === 'coach') return true;
    
    return false;
  },

  // Check if user can transfer head coach (admin, head coach, or coach - all have same access)
  async canTransferHeadCoach(teamId: string, userId: string): Promise<boolean> {
    const roleInfo = await this.getTeamMemberRole(teamId, userId);
    if (!roleInfo) return false;
    
    // Admin can always transfer
    if (roleInfo.isAdmin) return true;
    
    // Head coach can transfer
    if (roleInfo.role === 'headCoach') return true;
    
    // Regular coach can also transfer (same access as headCoach)
    if (roleInfo.role === 'coach') return true;
    
    return false;
  },

  // Check if user can assign roles (admin, head coach, or coach - all have same access)
  async canAssignRoles(teamId: string, userId: string): Promise<boolean> {
    const roleInfo = await this.getTeamMemberRole(teamId, userId);
    if (!roleInfo) return false;
    
    // Admin can always assign roles
    if (roleInfo.isAdmin) return true;
    
    // Head coach can assign roles
    if (roleInfo.role === 'headCoach') return true;
    
    // Regular coach can also assign roles (same access as headCoach)
    if (roleInfo.role === 'coach') return true;
    
    return false;
  },

  // Get head coach ID for a team
  async getHeadCoachId(teamId: string): Promise<string | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
        if (!teamDoc) return null;
        
        const teamData = teamDoc.data ? teamDoc.data() : teamDoc;
        // Support both headCoachId (new) and coachId (legacy) for backward compatibility
        return teamData.headCoachId || (teamData as any).coachId || null;
      } catch (error) {
        console.error('getHeadCoachId: Error via REST API:', error);
        return null;
      }
    }
    
    // Web/Android - use Firebase SDK
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    if (!teamDoc.exists()) return null;
    
    const teamData = teamDoc.data() as Team;
    // Support both headCoachId (new) and coachId (legacy) for backward compatibility
    return teamData.headCoachId || (teamData as any).coachId || null;
  },

  // Assign head coach role to a team member
  async assignHeadCoachRole(teamId: string, newHeadCoachId: string, assignedBy: string): Promise<void> {
    // Validate that assignedBy has permission (admin or current head coach)
    const canAssign = await this.canTransferHeadCoach(teamId, assignedBy);
    if (!canAssign) {
      throw new Error('You do not have permission to transfer head coach role');
    }

    // Get current head coach
    const currentHeadCoachId = await this.getHeadCoachId(teamId);
    if (!currentHeadCoachId) {
      throw new Error('Team does not have a head coach');
    }

    // Validate that new head coach is a team member
    const newHeadCoachMemberId = `${teamId}_${newHeadCoachId}`;
    const newHeadCoachMemberDoc = await getDoc(doc(db, 'teamMembers', newHeadCoachMemberId));
    if (!newHeadCoachMemberDoc.exists()) {
      throw new Error('New head coach must be a team member');
    }

    // Get team document
    const teamRef = doc(db, 'teams', teamId);
    const teamDoc = await getDoc(teamRef);
    if (!teamDoc.exists()) {
      throw new Error('Team not found');
    }

    const teamData = teamDoc.data() as Team;
    const previousHeadCoaches = teamData.previousHeadCoaches || [];

    // Update team: set new head coach and add old one to history
    await updateDoc(teamRef, {
      headCoachId: newHeadCoachId,
      previousHeadCoaches: [...previousHeadCoaches, currentHeadCoachId],
      updatedAt: serverTimestamp(),
    });

    // Update old head coach's role to 'coach' (preserve admin status if applicable)
    const oldHeadCoachMemberId = `${teamId}_${currentHeadCoachId}`;
    const oldHeadCoachMemberRef = doc(db, 'teamMembers', oldHeadCoachMemberId);
    const oldHeadCoachMemberDoc = await getDoc(oldHeadCoachMemberRef);
    if (oldHeadCoachMemberDoc.exists()) {
      await updateDoc(oldHeadCoachMemberRef, {
        role: 'coach',
        assignedBy,
        assignedAt: serverTimestamp(),
      });
    }

    // Update new head coach's role
    const newHeadCoachMemberRef = doc(db, 'teamMembers', newHeadCoachMemberId);
    await updateDoc(newHeadCoachMemberRef, {
      role: 'headCoach',
      assignedBy,
      assignedAt: serverTimestamp(),
    });
  },

  // Assign coach role to a team member
  async assignCoachRole(teamId: string, memberId: string, assignedBy: string): Promise<void> {
    // Validate that assignedBy has permission (admin or head coach)
    const canAssign = await this.canAssignRoles(teamId, assignedBy);
    if (!canAssign) {
      throw new Error('You do not have permission to assign coach role');
    }

    // Validate that member is a team member
    const memberRef = doc(db, 'teamMembers', memberId);
    const memberDoc = await getDoc(memberRef);
    if (!memberDoc.exists()) {
      throw new Error('Member not found');
    }

    const memberData = memberDoc.data() as TeamMember;
    if (memberData.teamId !== teamId) {
      throw new Error('Member does not belong to this team');
    }

    // Cannot assign coach role to current head coach
    if (memberData.role === 'headCoach') {
      throw new Error('Cannot assign coach role to head coach');
    }

    // Update member role
    await updateDoc(memberRef, {
      role: 'coach',
      assignedBy,
      assignedAt: serverTimestamp(),
    });
  },

  // Remove coach role from a team member
  async removeCoachRole(teamId: string, memberId: string, removedBy: string): Promise<void> {
    // Validate that removedBy has permission (admin or head coach)
    const canAssign = await this.canAssignRoles(teamId, removedBy);
    if (!canAssign) {
      throw new Error('You do not have permission to remove coach role');
    }

    // Validate that member is a team member
    const memberRef = doc(db, 'teamMembers', memberId);
    const memberDoc = await getDoc(memberRef);
    if (!memberDoc.exists()) {
      throw new Error('Member not found');
    }

    const memberData = memberDoc.data() as TeamMember;
    if (memberData.teamId !== teamId) {
      throw new Error('Member does not belong to this team');
    }

    // Cannot remove head coach role (must transfer first)
    if (memberData.role === 'headCoach') {
      throw new Error('Cannot remove head coach role. You must transfer it first.');
    }

    // Update member role to athlete
    await updateDoc(memberRef, {
      role: 'athlete',
      assignedBy: removedBy,
      assignedAt: serverTimestamp(),
    });
  },

  // Assign admin role to a team member
  async assignAdminRole(teamId: string, memberId: string, assignedBy: string): Promise<void> {
    // Validate that assignedBy has permission (admin or head coach)
    const canAssign = await this.canAssignRoles(teamId, assignedBy);
    if (!canAssign) {
      throw new Error('You do not have permission to assign admin role');
    }

    // Validate that member is a team member
    const memberRef = doc(db, 'teamMembers', memberId);
    const memberDoc = await getDoc(memberRef);
    if (!memberDoc.exists()) {
      throw new Error('Member not found');
    }

    const memberData = memberDoc.data() as TeamMember;
    if (memberData.teamId !== teamId) {
      throw new Error('Member does not belong to this team');
    }

    // Update member admin status (preserve base role)
    await updateDoc(memberRef, {
      isAdmin: true,
      adminAssignedBy: assignedBy,
      adminAssignedAt: serverTimestamp(),
    });
  },

  // Remove admin role from a team member
  async removeAdminRole(teamId: string, memberId: string, removedBy: string): Promise<void> {
    // Validate that removedBy has permission (admin or head coach)
    const canAssign = await this.canAssignRoles(teamId, removedBy);
    if (!canAssign) {
      throw new Error('You do not have permission to remove admin role');
    }

    // Validate that member is a team member
    const memberRef = doc(db, 'teamMembers', memberId);
    const memberDoc = await getDoc(memberRef);
    if (!memberDoc.exists()) {
      throw new Error('Member not found');
    }

    const memberData = memberDoc.data() as TeamMember;
    if (memberData.teamId !== teamId) {
      throw new Error('Member does not belong to this team');
    }

    // Update member admin status (preserve base role)
    await updateDoc(memberRef, {
      isAdmin: false,
      adminAssignedBy: removedBy,
      adminAssignedAt: serverTimestamp(),
    });
  },
};

